import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
	FaInstagram,
	FaFacebookF,
	FaXTwitter,
	FaLinkedinIn,
	FaLink,
	FaTrashCan,
	FaCircleCheck,
	FaCircleXmark,
	FaArrowUpRightFromSquare,
	FaPlus,
} from "react-icons/fa6";
import axios from "../../../components/Hooks/axios";
import { setUserObject } from "../../../redux/slices/authSlice";
import styles from "./SocialLinks.module.scss";

const SUGGESTED_PLATFORMS = [
	{ id: "instagram", name: "Instagram", icon: FaInstagram, color: "#E1306C", prefix: "instagram.com/" },
	{ id: "facebook", name: "Facebook", icon: FaFacebookF, color: "#1877F2", prefix: "facebook.com/" },
	{ id: "x", name: "X", icon: FaXTwitter, color: "#111111", prefix: "x.com/" },
	{ id: "linkedin", name: "LinkedIn", icon: FaLinkedinIn, color: "#0A66C2", prefix: "linkedin.com/in/" },
];

const getPlatformMeta = (platformName = "") => {
	const lower = platformName.trim().toLowerCase();
	const matched = SUGGESTED_PLATFORMS.find(
		(p) => p.id === lower || p.name.toLowerCase() === lower || (lower === "twitter" && p.id === "x")
	);
	return (
		matched || {
			id: lower,
			name: platformName,
			icon: FaLink,
			color: "#A6539D",
			prefix: `${lower}.com/`,
		}
	);
};

export const SocialLinksSection = ({ isEmbedded = false }) => {
	const dispatch = useDispatch();
	const { userId, user } = useSelector((state) => state.auth);

	const [platformInput, setPlatformInput] = useState("");
	const [usernameInput, setUsernameInput] = useState("");
	const [showDropdown, setShowDropdown] = useState(false);
	const [loading, setLoading] = useState(false);
	const [actionLoadingPlatform, setActionLoadingPlatform] = useState(null);
	const [links, setLinks] = useState(user?.social_links || []);

	const dropdownRef = useRef(null);
	const inputRef = useRef(null);

	// Sync with user's social_links
	useEffect(() => {
		if (user?.social_links) {
			setLinks(user.social_links);
		}
	}, [user]);

	// Fetch latest user details on mount
	useEffect(() => {
		if (!userId) return;
		axios
			.get(`/auth/get-user-details/${userId}`)
			.then(({ data }) => {
				dispatch(setUserObject(data));
				if (data.social_links) {
					setLinks(data.social_links);
				}
			})
			.catch((err) => {
				console.error("Error fetching user details:", err);
			});
	}, [userId, dispatch]);

	// Close suggestion dropdown on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				inputRef.current &&
				!inputRef.current.contains(event.target)
			) {
				setShowDropdown(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Filter suggestions
	const filteredSuggestions = SUGGESTED_PLATFORMS.filter((p) =>
		p.name.toLowerCase().includes(platformInput.trim().toLowerCase())
	);

	const handleSelectPlatform = (platform) => {
		setPlatformInput(platform.name);
		setShowDropdown(false);
	};

	// Connect account
	const handleConnect = (platformToConnect, usernameToConnect) => {
		const targetPlatform = platformToConnect || platformInput;
		const targetUsername = usernameToConnect || usernameInput;

		if (!targetPlatform.trim()) {
			toast.error("Please select or enter a platform name");
			return;
		}
		if (!targetUsername.trim()) {
			toast.error("Please enter your username");
			return;
		}

		setLoading(true);
		setActionLoadingPlatform(targetPlatform);

		axios
			.post("/auth/social-links/connect", {
				platform: targetPlatform.trim(),
				username: targetUsername.trim(),
			})
			.then(({ data }) => {
				toast.success(data.message || `${targetPlatform} connected successfully!`);
				if (data.user) {
					dispatch(setUserObject(data.user));
					setLinks(data.user.social_links || []);
				} else if (data.social_links) {
					setLinks(data.social_links);
				}
				// Reset inputs if connecting from main form
				if (!platformToConnect) {
					setPlatformInput("");
					setUsernameInput("");
				}
			})
			.catch((err) => {
				console.error("Connect error:", err);
				toast.error(err.response?.data?.message || "Failed to connect account");
			})
			.finally(() => {
				setLoading(false);
				setActionLoadingPlatform(null);
			});
	};

	// Disconnect account
	const handleDisconnect = (platform) => {
		setActionLoadingPlatform(platform);

		axios
			.post("/auth/social-links/disconnect", { platform })
			.then(({ data }) => {
				toast.info(data.message || `${platform} disconnected`);
				if (data.user) {
					dispatch(setUserObject(data.user));
					setLinks(data.user.social_links || []);
				} else if (data.social_links) {
					setLinks(data.social_links);
				}
			})
			.catch((err) => {
				console.error("Disconnect error:", err);
				toast.error(err.response?.data?.message || "Failed to disconnect account");
			})
			.finally(() => {
				setActionLoadingPlatform(null);
			});
	};

	// Delete account link
	const handleDelete = (platform) => {
		setActionLoadingPlatform(platform);

		axios
			.delete(`/auth/social-links/${encodeURIComponent(platform)}`)
			.then(({ data }) => {
				toast.success(data.message || `${platform} link removed`);
				if (data.user) {
					dispatch(setUserObject(data.user));
					setLinks(data.user.social_links || []);
				} else if (data.social_links) {
					setLinks(data.social_links);
				}
			})
			.catch((err) => {
				console.error("Delete error:", err);
				toast.error(err.response?.data?.message || "Failed to remove link");
			})
			.finally(() => {
				setActionLoadingPlatform(null);
			});
	};

	// Dynamic button text
	const currentPlatform = platformInput.trim();
	const connectButtonLabel = currentPlatform
		? `Connect ${currentPlatform}`
		: "Connect / Link Account";

	return (
		<div className={`${styles.SocialLinksWrapper} ${isEmbedded ? styles.EmbeddedSection : ""}`}>
			{/* Main Link Form Card */}
			<div className={styles.Card}>
				<div className={styles.CardHeader}>
					<h2>Link Social Media Account</h2>
					<p>Choose a platform, provide your username handle, and click connect.</p>
				</div>

				{/* Quick Platform Chips */}
				<div className={styles.QuickChips}>
					<span className={styles.QuickLabel}>Suggested:</span>
					{SUGGESTED_PLATFORMS.map((item) => {
						const Icon = item.icon;
						const isSelected =
							platformInput.trim().toLowerCase() === item.name.toLowerCase();
						return (
							<button
								key={item.id}
								type="button"
								className={`${styles.Chip} ${isSelected ? styles.ActiveChip : ""}`}
								onClick={() => handleSelectPlatform(item)}
							>
								<Icon style={{ color: item.color }} />
								<span>{item.name}</span>
							</button>
						);
					})}
				</div>

				<div className={styles.FormGrid}>
					{/* Platform Input with Autocomplete Suggestions */}
					<div className={styles.FormGroup} ref={dropdownRef}>
						<label className={styles.Label}>Platform Name</label>
						<div className={styles.InputWrapper}>
							<input
								ref={inputRef}
								type="text"
								className={styles.Input}
								placeholder="e.g. Instagram, Facebook, X..."
								value={platformInput}
								onChange={(e) => {
									setPlatformInput(e.target.value);
									setShowDropdown(true);
								}}
								onFocus={() => setShowDropdown(true)}
							/>
						</div>

						{/* Autocomplete Dropdown */}
						{showDropdown && filteredSuggestions.length > 0 && (
							<div className={styles.Dropdown}>
								{filteredSuggestions.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.id}
											className={styles.DropdownItem}
											onClick={() => handleSelectPlatform(item)}
										>
											<Icon
												className={styles.DropdownIcon}
												style={{ color: item.color }}
											/>
											<span className={styles.DropdownText}>{item.name}</span>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{/* Username Input */}
					<div className={styles.FormGroup}>
						<label className={styles.Label}>Username</label>
						<div className={styles.InputWrapper}>
							<span className={styles.InputPrefix}>@</span>
							<input
								type="text"
								className={`${styles.Input} ${styles.InputWithPrefix}`}
								placeholder="username"
								value={usernameInput}
								onChange={(e) => setUsernameInput(e.target.value.replace(/^@/, ""))}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										handleConnect();
									}
								}}
							/>
						</div>
					</div>
				</div>

				{/* Connect / Link Button */}
				<div className={styles.FormActions}>
					<button
						type="button"
						className={styles.ConnectBtn}
						onClick={() => handleConnect()}
						disabled={loading || !platformInput.trim() || !usernameInput.trim()}
					>
						{loading ? (
							<span>Connecting...</span>
						) : (
							<>
								<FaPlus />
								<span>{connectButtonLabel}</span>
							</>
						)}
					</button>
				</div>
			</div>

			{/* Linked Accounts Card */}
			<div className={styles.Card}>
				<div className={styles.CardHeader}>
					<h2>Connected Accounts ({links.length})</h2>
					<p>Manage your linked platforms and connection status.</p>
				</div>

				{links.length === 0 ? (
					<div className={styles.EmptyState}>
						<FaLink className={styles.EmptyIcon} />
						<h3>No Social Links Connected</h3>
						<p>Add your Instagram, Facebook, or X accounts above to link them.</p>
					</div>
				) : (
					<div className={styles.AccountsList}>
						{links.map((linkItem) => {
							const meta = getPlatformMeta(linkItem.platform);
							const Icon = meta.icon;
							const isActionLoading = actionLoadingPlatform === linkItem.platform;

							return (
								<div key={linkItem.platform} className={styles.AccountItem}>
									{/* Platform Info */}
									<div className={styles.AccountLeft}>
										<div
											className={styles.PlatformIconBadge}
											style={{ backgroundColor: `${meta.color}18`, color: meta.color }}
										>
											<Icon />
										</div>

										<div className={styles.AccountDetails}>
											<div className={styles.AccountTitleRow}>
												<h3 className={styles.AccountPlatformName}>
													{linkItem.platform}
												</h3>

												{/* Connection Status Badge */}
												{linkItem.connected ? (
													<span className={`${styles.StatusBadge} ${styles.StatusConnected}`}>
														<FaCircleCheck className={styles.StatusIcon} />
														<span>Connected</span>
													</span>
												) : (
													<span className={`${styles.StatusBadge} ${styles.StatusDisconnected}`}>
														<FaCircleXmark className={styles.StatusIcon} />
														<span>Not connected</span>
													</span>
												)}
											</div>

											<div className={styles.AccountMetaRow}>
												<span className={styles.AccountHandle}>
													@{linkItem.username}
												</span>

												{linkItem.profileUrl && (
													<a
														href={linkItem.profileUrl}
														target="_blank"
														rel="noopener noreferrer"
														className={styles.ProfileLink}
														title="Visit profile"
													>
														<span>View profile</span>
														<FaArrowUpRightFromSquare />
													</a>
												)}
											</div>
										</div>
									</div>

									{/* Actions: Connect / Disconnect / Remove */}
									<div className={styles.AccountRight}>
										{linkItem.connected ? (
											<button
												type="button"
												className={styles.DisconnectBtn}
												onClick={() => handleDisconnect(linkItem.platform)}
												disabled={isActionLoading}
											>
												{isActionLoading ? "Please wait..." : "Disconnect"}
											</button>
										) : (
											<button
												type="button"
												className={styles.ReconnectBtn}
												onClick={() =>
													handleConnect(linkItem.platform, linkItem.username)
												}
												disabled={isActionLoading}
											>
												{isActionLoading
													? "Please wait..."
													: `Connect ${linkItem.platform}`}
											</button>
										)}

										<button
											type="button"
											className={styles.DeleteBtn}
											onClick={() => handleDelete(linkItem.platform)}
											disabled={isActionLoading}
											title="Remove connection"
										>
											<FaTrashCan />
										</button>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default SocialLinksSection;
